import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  AttachMoney,
  Assessment,
  Calculate,
  CheckCircle,
  Warning,
  Edit,
  TrendingUp,
  ExpandMore,
  Gavel,
  Info
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const VentaFase1Tracker = () => {
  const [tasaCambio, setTasaCambio] = useState(4300);
  const [ventaUSD, setVentaUSD] = useState(10000);
  const [serviciosUSD, setServiciosUSD] = useState(25000);
  const [objetivoCOP, setObjetivoCOP] = useState(110000000);
  const [deduccionesUSD, setDeduccionesUSD] = useState(0); // Deducciones permitidas
  
  const totalVenta = ventaUSD + serviciosUSD;
  
  // Cálculos tributarios CORREGIDOS
  const impVenta = ventaUSD * 0.10; // Ganancia ocasional 10%
  
  // Servicios: SIN exención automática del 25%
  const baseGravableServicios = serviciosUSD - deduccionesUSD;
  const impServicios = baseGravableServicios * 0.35; // Tarifa marginal estimada 35%
  
  const totalImp = impVenta + impServicios;
  const netoUSD = totalVenta - totalImp;
  const netoCOP = netoUSD * tasaCambio;
  const diferenciaCOP = netoCOP - objetivoCOP;

  const formatCOP = (num) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatUSD = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        {/* Alerta Legal Importante */}
        <Alert severity="error" icon={<Gavel />} sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            ⚖️ CORRECCIÓN LEGAL IMPORTANTE
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>NO existe una exención automática del 25%</strong> para servicios profesionales en Colombia. 
            La versión anterior contenía un error legal.
          </Typography>
          <Typography variant="body2">
            Los servicios profesionales tributan sobre el <strong>100% del ingreso</strong> menos las deducciones 
            permitidas por ley (Art. 107 ET: costos, gastos necesarios, intereses de vivienda, medicina prepagada, etc.).
          </Typography>
        </Alert>

        {/* Header */}
        <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Calculate sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Calculadora Venta Tracker ON
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Cálculo tributario CORREGIDO según normativa colombiana vigente
              </Typography>
            </Box>
          </Box>

          {/* Panel de Controles */}
          <Box sx={{ 
            background: 'linear-gradient(to right, #f3e5f5, #e3f2fd)',
            borderRadius: 2,
            p: 3,
            border: '2px solid',
            borderColor: 'primary.light',
            mb: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Edit sx={{ color: 'primary.main' }} />
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                Panel de Control - Valores Editables
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Venta Software (USD)"
                  type="number"
                  fullWidth
                  value={ventaUSD}
                  onChange={(e) => setVentaUSD(Number(e.target.value))}
                  variant="outlined"
                  helperText="Ganancia ocasional 10%"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Servicios Profesionales (USD)"
                  type="number"
                  fullWidth
                  value={serviciosUSD}
                  onChange={(e) => setServiciosUSD(Number(e.target.value))}
                  variant="outlined"
                  helperText="100% gravable"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Deducciones (USD)"
                  type="number"
                  fullWidth
                  value={deduccionesUSD}
                  onChange={(e) => setDeduccionesUSD(Number(e.target.value))}
                  variant="outlined"
                  helperText="Según Art. 107 ET"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Tasa Cambio (COP/USD)"
                  type="number"
                  fullWidth
                  value={tasaCambio}
                  onChange={(e) => setTasaCambio(Number(e.target.value))}
                  variant="outlined"
                  helperText="Actual"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Objetivo Neto (COP)"
                  type="number"
                  fullWidth
                  value={objetivoCOP}
                  onChange={(e) => setObjetivoCOP(Number(e.target.value))}
                  variant="outlined"
                  helperText="Tu meta"
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }} icon={<Info />}>
              <Typography variant="body2">
                <strong>💡 Deducciones permitidas:</strong> Costos y gastos necesarios para producir la renta, 
                intereses de vivienda, medicina prepagada, dependientes, aportes pensionales voluntarios (hasta límites legales).
              </Typography>
            </Alert>
          </Box>
          
          {/* Resumen Principal */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Valor Total Venta
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {formatUSD(totalVenta)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCOP(totalVenta * tasaCambio)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'success.50', borderLeft: 4, borderColor: 'success.main' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Objetivo Neto
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {formatCOP(objetivoCOP)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'secondary.50', borderLeft: 4, borderColor: 'secondary.main' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Neto Real
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="secondary.main">
                    {formatCOP(netoCOP)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                bgcolor: diferenciaCOP >= 0 ? 'warning.50' : 'error.50', 
                borderLeft: 4, 
                borderColor: diferenciaCOP >= 0 ? 'warning.main' : 'error.main' 
              }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {diferenciaCOP >= 0 ? 'Excedente' : 'Faltante'}
                  </Typography>
                  <Typography 
                    variant="h5" 
                    fontWeight="bold" 
                    color={diferenciaCOP >= 0 ? 'warning.main' : 'error.main'}
                  >
                    {formatCOP(Math.abs(diferenciaCOP))}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Alert de cumplimiento */}
        <Alert 
          severity={diferenciaCOP >= 0 ? 'success' : 'warning'}
          icon={diferenciaCOP >= 0 ? <CheckCircle /> : <Warning />}
          sx={{ mb: 3 }}
        >
          <Typography variant="h6" fontWeight="bold">
            {diferenciaCOP >= 0 ? '✅ Cumples tu objetivo' : '⚠️ No alcanzas tu objetivo'}
          </Typography>
          <Typography>
            {diferenciaCOP >= 0 
              ? `Superas por ${formatCOP(diferenciaCOP)} (+${((diferenciaCOP/objetivoCOP)*100).toFixed(2)}%)`
              : `Faltan ${formatCOP(Math.abs(diferenciaCOP))} para llegar a tu objetivo (${((diferenciaCOP/objetivoCOP)*100).toFixed(2)}%)`
            }
          </Typography>
        </Alert>

        {/* Marco Legal */}
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Gavel color="primary" />
              <Typography variant="h6" fontWeight="bold">
                📚 Sustento Legal - Normativa Colombiana
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'primary.50' }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main" gutterBottom>
                    1. Ganancia Ocasional (Venta de Software)
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Base legal:</strong> Art. 299-317 Estatuto Tributario
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Tarifa:</strong> 10% sobre el valor de la venta
                  </Typography>
                  <Typography variant="body2">
                    <strong>Aplicable a:</strong> Venta de bienes (activos fijos, propiedad intelectual, código fuente)
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'success.50' }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="success.main" gutterBottom>
                    2. Servicios Profesionales (Renta Ordinaria)
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Base legal:</strong> Art. 26-27, 107, 241 Estatuto Tributario
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Tarifa:</strong> Tabla progresiva (19%-39% según rangos)
                  </Typography>
                  <Typography variant="body2">
                    <strong>❌ NO existe exención automática del 25%</strong>
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'warning.50' }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="warning.main" gutterBottom>
                    3. Deducciones Permitidas (Art. 107 ET)
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">• Costos y gastos necesarios para producir renta</Typography>
                      <Typography variant="body2">• Intereses por crédito de vivienda</Typography>
                      <Typography variant="body2">• Medicina prepagada y seguros de salud</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">• Dependientes económicos</Typography>
                      <Typography variant="body2">• Aportes voluntarios a pensión (hasta 30% ingreso)</Typography>
                      <Typography variant="body2">• Otros gastos deducibles según ley</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Distribución */}
        <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Assessment sx={{ color: 'primary.main' }} />
            <Typography variant="h5" fontWeight="bold">
              Distribución de la Venta
            </Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight="bold">Composición del pago total</Typography>
              <Typography variant="body2" fontWeight="bold">{formatUSD(totalVenta)}</Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              height: 32, 
              borderRadius: 1, 
              overflow: 'hidden',
              mb: 1
            }}>
              <Box sx={{ 
                bgcolor: 'primary.main', 
                width: `${(ventaUSD/totalVenta)*100}%`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 'bold'
              }}>
                {((ventaUSD/totalVenta)*100).toFixed(1)}%
              </Box>
              <Box sx={{ 
                bgcolor: 'success.main', 
                width: `${(serviciosUSD/totalVenta)*100}%`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 'bold'
              }}>
                {((serviciosUSD/totalVenta)*100).toFixed(1)}%
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
              <Typography variant="caption">🔵 Venta: {formatUSD(ventaUSD)}</Typography>
              <Typography variant="caption">🟢 Servicios: {formatUSD(serviciosUSD)}</Typography>
            </Box>
          </Box>

          <Grid container spacing={3} sx={{ display: 'flex', alignItems: 'stretch' }}>
            {/* Factura 1 */}
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Card sx={{ 
                bgcolor: 'primary.50',
                border: 2,
                borderColor: 'primary.main',
                height: '100%', // Forzar misma altura
                display: 'flex',
                flexDirection: 'column'
              }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                      📄 FACTURA 1
                    </Typography>
                    <Chip label="Ganancia Ocasional" color="primary" size="small" />
                  </Box>
                  
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Concepto:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1 }}>
                    Cesión de derechos de propiedad intelectual y código fuente
                  </Typography>
                  
                  <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography fontWeight="bold">Valor:</Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h5" fontWeight="bold" color="primary.main">
                          {formatUSD(ventaUSD)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCOP(ventaUSD * tasaCambio)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1, border: 1, borderColor: 'primary.light' }}>
                    <Typography variant="caption" color="text.secondary">
                      Impuesto a pagar (10%):
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="error.main">
                      {formatUSD(impVenta)}
                    </Typography>
                    <Typography variant="caption" color="secondary.main">
                      {formatCOP(impVenta * tasaCambio)}
                    </Typography>
                  </Box>

                  <Alert severity="info" sx={{ mt: 2 }} icon={<Gavel fontSize="small" />}>
                    <Typography variant="caption">
                      <strong>Base legal:</strong> Art. 299 ET - Ganancia Ocasional 10%
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            </Grid>

            {/* Factura 2 */}
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                bgcolor: 'success.50',
                border: 2,
                borderColor: 'success.main',
                height: '100%', // Forzar misma altura
                display: 'flex',
                flexDirection: 'column'
              }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                      📄 FACTURA 2
                    </Typography>
                    <Chip label="Servicios Profesionales" color="success" size="small" />
                  </Box>
                  
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Conceptos:
                  </Typography>
                  <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, mb: 2 }}>
                    <Typography variant="body2">✓ Desarrollo y programación</Typography>
                    <Typography variant="body2">✓ Documentación técnica</Typography>
                    <Typography variant="body2">✓ Capacitación y soporte</Typography>
                  </Box>
                  
                  <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography fontWeight="bold">Valor bruto:</Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                          {formatUSD(serviciosUSD)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCOP(serviciosUSD * tasaCambio)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1, border: 1, borderColor: 'success.light' }}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Menos: Deducciones (Art. 107):
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="info.main">
                        -{formatUSD(deduccionesUSD)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Base gravable:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatUSD(baseGravableServicios)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Impuesto (35% tarifa marginal):
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="error.main">
                        {formatUSD(impServicios)}
                      </Typography>
                      <Typography variant="caption" color="secondary.main">
                        {formatCOP(impServicios * tasaCambio)}
                      </Typography>
                    </Box>
                  </Box>

                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Alerta Legal debajo de ambos cards */}
          <Alert severity="warning" icon={<Warning />} sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>⚠️ IMPORTANTE:</strong> NO existe exención automática del 25% para servicios profesionales. 
              Se aplica tarifa completa según Art. 241 ET sobre la base gravable (ingresos menos deducciones permitidas).
            </Typography>
          </Alert>
        </Paper>

        {/* Tabla Detallada */}
        <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <AttachMoney sx={{ color: 'warning.main' }} />
            <Typography variant="h5" fontWeight="bold">
              Cálculo Tributario Detallado (CORREGIDO)
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Concepto</strong></TableCell>
                  <TableCell align="right"><strong>Monto USD</strong></TableCell>
                  <TableCell align="right"><strong>Monto COP</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell><strong>Venta de Software</strong></TableCell>
                  <TableCell align="right">{formatUSD(ventaUSD)}</TableCell>
                  <TableCell align="right">{formatCOP(ventaUSD * tasaCambio)}</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell sx={{ pl: 4, color: 'error.main' }}>→ Impuesto (10% - Art. 299 ET)</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>-{formatUSD(impVenta)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>-{formatCOP(impVenta * tasaCambio)}</TableCell>
                </TableRow>
                
                <TableRow sx={{ bgcolor: 'success.50' }}>
                  <TableCell><strong>Servicios Profesionales</strong></TableCell>
                  <TableCell align="right">{formatUSD(serviciosUSD)}</TableCell>
                  <TableCell align="right">{formatCOP(serviciosUSD * tasaCambio)}</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'success.50' }}>
                  <TableCell sx={{ pl: 4, color: 'info.main' }}>→ Deducciones (Art. 107 ET)</TableCell>
                  <TableCell align="right" sx={{ color: 'info.main' }}>-{formatUSD(deduccionesUSD)}</TableCell>
                  <TableCell align="right" sx={{ color: 'info.main' }}>-{formatCOP(deduccionesUSD * tasaCambio)}</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'success.50' }}>
                  <TableCell sx={{ pl: 4 }}>→ Base gravable (100% - deducciones)</TableCell>
                  <TableCell align="right">{formatUSD(baseGravableServicios)}</TableCell>
                  <TableCell align="right">{formatCOP(baseGravableServicios * tasaCambio)}</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'success.50' }}>
                  <TableCell sx={{ pl: 4, color: 'error.main' }}>→ Impuesto (35% tarifa marginal - Art. 241 ET)</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>-{formatUSD(impServicios)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>-{formatCOP(impServicios * tasaCambio)}</TableCell>
                </TableRow>
                
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>TOTAL BRUTO</strong></TableCell>
                  <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{formatUSD(totalVenta)}</TableCell>
                  <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{formatCOP(totalVenta * tasaCambio)}</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'error.50' }}>
                  <TableCell><strong>TOTAL IMPUESTOS</strong></TableCell>
                  <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>-{formatUSD(totalImp)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>-{formatCOP(totalImp * tasaCambio)}</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'success.100' }}>
                  <TableCell><Typography variant="h6"><strong>NETO PARA TI</strong></Typography></TableCell>
                  <TableCell align="right"><Typography variant="h6" color="success.main" fontWeight="bold">{formatUSD(netoUSD)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="h6" color="success.main" fontWeight="bold">{formatCOP(netoCOP)}</Typography></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mt: 3 }}>
            <Grid container spacing={2} textAlign="center">
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">Carga Tributaria</Typography>
                <Typography variant="h6" fontWeight="bold">{((totalImp/totalVenta)*100).toFixed(2)}%</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">% Retenido</Typography>
                <Typography variant="h6" fontWeight="bold">{(((totalVenta - netoUSD)/totalVenta)*100).toFixed(2)}%</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">% Neto</Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">{((netoUSD/totalVenta)*100).toFixed(2)}%</Typography>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Resultado Final */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            mb: 3,
            background: diferenciaCOP >= 0 
              ? 'linear-gradient(135deg, #43a047, #66bb6a)'
              : 'linear-gradient(135deg, #f57c00, #ff9800)',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            {diferenciaCOP >= 0 ? <CheckCircle sx={{ fontSize: 40 }} /> : <Warning sx={{ fontSize: 40 }} />}
            <Typography variant="h4" fontWeight="bold">
              Resultado Final
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', p: 3, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                  Total Bruto
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {formatUSD(totalVenta)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                  {formatCOP(totalVenta * tasaCambio)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', p: 3, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                  Menos: Impuestos
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  -{formatUSD(totalImp)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7, mt: 1 }}>
                  ({((totalImp/totalVenta)*100).toFixed(2)}% del total)
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                bgcolor: 'rgba(255,255,255,0.3)', 
                p: 3, 
                borderRadius: 2,
                border: '2px solid white'
              }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  💰 NETO PARA TI
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {formatUSD(netoUSD)}
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                  {formatCOP(netoCOP)}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', p: 2, borderRadius: 1, mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body1">
                Tu objetivo: {formatCOP(objetivoCOP)}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {diferenciaCOP >= 0 
                  ? `✅ Superas por ${formatCOP(diferenciaCOP)}`
                  : `⚠️ Faltan ${formatCOP(Math.abs(diferenciaCOP))}`
                }
              </Typography>
            </Box>
          </Box>

            <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', p: 2, borderRadius: 1, mt: 3 }}>
                <Link to="/comisiones" style={{ textDecoration: 'none' }}>
                    <Typography variant="body1" sx={{ color: 'inherit', textAlign: 'center' }}>
                    Análisis de Comisiones
                    </Typography>
                </Link>
            </Box>
        </Paper>

        {/* Recomendaciones para alcanzar objetivo */}
        {diferenciaCOP < 0 && (
          <Alert severity="info" icon={<TrendingUp />} sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              💡 Sugerencias para alcanzar tu objetivo de {formatCOP(objetivoCOP)}
            </Typography>
            <Typography variant="body2" paragraph>
              Opciones para aumentar tu neto:
            </Typography>
            <Box component="ul" sx={{ ml: 2 }}>
              <li>
                <strong>Aumentar valor total:</strong> Necesitarías vender por ~{formatUSD(Math.ceil((objetivoCOP/tasaCambio + totalImp) / 100) * 100)} USD
              </li>
              <li>
                <strong>Aumentar deducciones:</strong> Incluye todos los gastos deducibles permitidos (costos, medicina prepagada, intereses, etc.)
              </li>
              <li>
                <strong>Ajustar proporción:</strong> Más venta (10%) y menos servicios (35%) reduce carga tributaria total
              </li>
              <li>
                <strong>Consultar contador:</strong> Puede identificar deducciones específicas para tu situación
              </li>
            </Box>
          </Alert>
        )}

        {/* Comparación vs cálculo anterior ERRÓNEO */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: 'warning.50', border: 2, borderColor: 'warning.main' }}>
          <Typography variant="h6" fontWeight="bold" color="warning.dark" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            Comparación: Cálculo Correcto vs Erróneo (con falsa exención 25%)
          </Typography>
          
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.200' }}>
                  <TableCell><strong>Concepto</strong></TableCell>
                  <TableCell align="right"><strong>❌ Cálculo Anterior (ERRÓNEO)</strong></TableCell>
                  <TableCell align="right"><strong>✅ Cálculo Correcto (SIN exención)</strong></TableCell>
                  <TableCell align="right"><strong>Diferencia</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Servicios brutos</TableCell>
                  <TableCell align="right">{formatUSD(serviciosUSD)}</TableCell>
                  <TableCell align="right">{formatUSD(serviciosUSD)}</TableCell>
                  <TableCell align="right">-</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>"Exención 25%"</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>-{formatUSD(serviciosUSD * 0.25)} ❌</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>$0.00 ✅</TableCell>
                  <TableCell align="right">-</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Base gravable servicios</TableCell>
                  <TableCell align="right">{formatUSD(serviciosUSD * 0.75)}</TableCell>
                  <TableCell align="right">{formatUSD(baseGravableServicios)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>{formatUSD(baseGravableServicios - serviciosUSD * 0.75)}</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Impuesto servicios</strong></TableCell>
                  <TableCell align="right">{formatUSD((serviciosUSD * 0.75) * 0.35)}</TableCell>
                  <TableCell align="right">{formatUSD(impServicios)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>+{formatUSD(impServicios - (serviciosUSD * 0.75) * 0.35)}</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'warning.100' }}>
                  <TableCell><strong>NETO FINAL</strong></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCOP((totalVenta - impVenta - (serviciosUSD * 0.75) * 0.35) * tasaCambio)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCOP(netoCOP)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>{formatCOP(netoCOP - (totalVenta - impVenta - (serviciosUSD * 0.75) * 0.35) * tasaCambio)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Conclusión:</strong> La falsa "exención del 25%" hacía que pagaras {formatUSD(Math.abs(impServicios - (serviciosUSD * 0.75) * 0.35))} USD 
              MENOS en impuestos, dando un neto {formatCOP(Math.abs(netoCOP - (totalVenta - impVenta - (serviciosUSD * 0.75) * 0.35) * tasaCambio))} COP 
              MÁS ALTO que el real. <strong>Esto es ilegal y podría resultar en sanciones de la DIAN.</strong>
            </Typography>
          </Alert>
        </Paper>

        {/* Nota final */}
        <Paper elevation={3} sx={{ 
          p: 3,
          background: 'linear-gradient(to right, #37474f, #263238)',
          color: 'white'
        }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Gavel />
            Advertencia Legal y Configuración Actual
          </Typography>
          
          <Box component="ul" sx={{ fontSize: '0.875rem', opacity: 0.9, mb: 2 }}>
            <li>Venta (Ganancia Ocasional): {formatUSD(ventaUSD)} ({((ventaUSD/totalVenta)*100).toFixed(1)}%) - Tarifa: 10%</li>
            <li>Servicios Profesionales: {formatUSD(serviciosUSD)} ({((serviciosUSD/totalVenta)*100).toFixed(1)}%) - Tarifa: 35%</li>
            <li>Deducciones aplicadas: {formatUSD(deduccionesUSD)}</li>
            <li>Tasa de cambio: {tasaCambio.toLocaleString()} COP/USD</li>
            <li>Objetivo neto: {formatCOP(objetivoCOP)}</li>
          </Box>

          <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)', my: 2 }} />

          <Typography variant="body2" paragraph sx={{ mb: 1 }}>
            <strong>⚖️ Base Legal:</strong>
          </Typography>
          <Box component="ul" sx={{ fontSize: '0.875rem', opacity: 0.9, ml: 2 }}>
            <li>Estatuto Tributario Art. 299-317 (Ganancia Ocasional)</li>
            <li>Estatuto Tributario Art. 26-27, 107, 241 (Renta Ordinaria y Deducciones)</li>
            <li>No existe exención automática del 25% para servicios profesionales</li>
          </Box>

          <Alert severity="warning" sx={{ mt: 2, bgcolor: 'rgba(255,193,7,0.1)', color: 'white', borderColor: 'warning.main' }}>
            <Typography variant="body2" fontWeight="bold">
              ⚠️ IMPORTANTE: Estos cálculos son estimados. La tarifa marginal real puede variar según tus otros ingresos 
              del año. Es OBLIGATORIO consultar con un contador público certificado antes de tomar decisiones fiscales.
            </Typography>
          </Alert>
        </Paper>
      </Container>
    </Box>
  );
};

export default VentaFase1Tracker;